import React, { useCallback, useRef, useState } from "react";
import { Input } from "../../../../../components/Input";
import { ReactComponent as Plus } from "../../../../../assets/newImages/icons/plus.svg";
import { ReactComponent as Copy } from "../../../../../assets/newImages/icons/copy.svg";
import { ReactComponent as Check } from "../../../../../assets/newImages/header/check.svg";
import { useTheme } from "../../../../../contexts/Theme/ThemeContext.tsx";
import { currencyMask, stringNumber, decimalNumber } from "../../../../../services/Masks.tsx";
import { unmaskToInt } from "../../../../../services/Unmasks.tsx";
import { VariacaoV2 } from "../../../../../types/Produto.ts";
import { GradeContainer } from "./styles.ts";
import Swal from "sweetalert2";
import LockedIcon from "../../../../../components/LockedIcon/LockedIcon.tsx";
import { ReactComponent as RestaurarIcon } from "../../../../../assets/newImages/icons/restaurar.svg";

interface GradeRowProps {
  variacao: VariacaoV2;
  index: number;
  temEstoque: boolean;
  permitir_edicao: boolean;
  listasDePreco: any[];
  onUpdateVariacao: (index: number, field: string, value: any) => void;
  onDeleteVariacao: (index: number) => void;
  onReplicar: (field: string, value: any, precoIndex?: number) => void;
  replicado: string | null;
  onReplicarClick: (key: string, callback: () => void) => void;
  primaryColor: string;
}

const GradeRow = React.memo<GradeRowProps>(({
  variacao,
  index,
  temEstoque,
  permitir_edicao,
  listasDePreco,
  onUpdateVariacao,
  onDeleteVariacao,
  onReplicar,
  replicado,
  onReplicarClick,
  primaryColor,
}) => {
  const replicarRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const handleFocus = (key: string) => {
    const el = replicarRefs.current.get(key);
    if (el) el.classList.add("visible");
  };

  const handleBlur = (key: string) => {
    const el = replicarRefs.current.get(key);
    if (el) el.classList.remove("visible");
  };

  const checkStyle = { width: 14, height: 14, stroke: primaryColor };

  return (
    <div className="grade-row">
      {/* SKU */}
      <div className="cell cell-lg">
        <Input
          type="text"
          name="sku"
          value={variacao.sku}
          disabled={!permitir_edicao}
          placeholder="SKU"
          onChange={(newValue: any) => onUpdateVariacao(index, "sku", newValue)}
          onFocus={() => handleFocus(`sku-${index}`)}
          onBlur={() => handleBlur(`sku-${index}`)}
        />
        <div
          className="replicar fonteRotulos"
          ref={(el) => replicarRefs.current.set(`sku-${index}`, el)}
          onPointerDown={(e) => { e.preventDefault(); onReplicarClick(`sku-${index}`, () => onReplicar("sku", variacao.sku)); }}
        >
          {replicado === `sku-${index}` ? <><Check style={checkStyle} /> Aplicado</> : <><Copy /> Aplicar em todas</>}
        </div>
      </div>

      {/* Variacao name (read-only) */}
      <div className="cell cell-lg">
        <Input
          type="text"
          name="variacao"
          value={variacao.variacao}
          disabled={true}
          placeholder="Variação"
          onChange={() => {}}
        />
      </div>

      {/* Estoque */}
      {temEstoque && (
        <div className="cell cell-sm">
          <Input
            type="text"
            name="estoque"
            value={variacao.estoque || "0"}
            disabled={!permitir_edicao}
            mask={{ mask: stringNumber }}
            placeholder="0"
            onChange={(newValue: any) => {
              if (newValue === "") newValue = "0";
              onUpdateVariacao(index, "estoque", newValue);
            }}
            onFocus={() => handleFocus(`estoque-${index}`)}
            onBlur={() => handleBlur(`estoque-${index}`)}
          />
          <div
            className="replicar fonteRotulos"
            ref={(el) => replicarRefs.current.set(`estoque-${index}`, el)}
            onPointerDown={(e) => { e.preventDefault(); onReplicarClick(`estoque-${index}`, () => onReplicar("estoque", variacao.estoque)); }}
          >
            {replicado === `estoque-${index}` ? <><Check style={checkStyle} /> Aplicado</> : <><Copy /> Aplicar em todas</>}
          </div>
        </div>
      )}

      {/* Peso */}
      <div className="cell cell-sm">
        <Input
          type="text"
          name="peso"
          value={variacao.peso?.toString() || ""}
          disabled={!permitir_edicao}
          mask={{ mask: decimalNumber }}
          placeholder="0"
          onChange={(newValue: any) => {
            if (newValue === "") newValue = null;
            onUpdateVariacao(index, "peso", newValue);
          }}
          onFocus={() => handleFocus(`peso-${index}`)}
          onBlur={() => handleBlur(`peso-${index}`)}
        />
        <div
          className="replicar fonteRotulos"
          ref={(el) => replicarRefs.current.set(`peso-${index}`, el)}
          onPointerDown={(e) => { e.preventDefault(); onReplicarClick(`peso-${index}`, () => onReplicar("peso", variacao.peso)); }}
        >
          {replicado === `peso-${index}` ? <><Check style={checkStyle} /> Aplicado</> : <><Copy /> Aplicar em todas</>}
        </div>
        <div className="conversor-peso fonteRotulos">
          {variacao.peso || 0}g = {(Number(String(variacao.peso || 0).replace(',', '.')) / 1000).toFixed(3).replace('.', ',')}kg
        </div>
      </div>

      {/* Precos */}
      {variacao.precos.map((preco, precoIdx) => (
        <div className="cell" key={precoIdx}>
          <Input
            type="text"
            name={`preco_${precoIdx}`}
            disabled={!permitir_edicao}
            mask={{
              mask: currencyMask,
              unmask: unmaskToInt,
              param:
                listasDePreco[precoIdx]?.moeda
                  ? listasDePreco[precoIdx].moeda
                  : "R$",
            }}
            value={preco.preco?.toString() || "0"}
            onChange={(newValue: any) =>
              onUpdateVariacao(index, `preco_${precoIdx}`, newValue)
            }
            onFocus={() => handleFocus(`preco-${index}-${precoIdx}`)}
            onBlur={() => handleBlur(`preco-${index}-${precoIdx}`)}
            placeholder="R$ 00,00"
          />
          <div
            className="replicar fonteRotulos"
            ref={(el) => replicarRefs.current.set(`preco-${index}-${precoIdx}`, el)}
            onPointerDown={(e) => { e.preventDefault(); onReplicarClick(`preco-${index}-${precoIdx}`, () => onReplicar("preco", preco.preco, precoIdx)); }}
          >
            {replicado === `preco-${index}-${precoIdx}` ? <><Check style={checkStyle} /> Aplicado</> : <><Copy /> Aplicar em todas</>}
          </div>
        </div>
      ))}

      {/* Delete */}
      {permitir_edicao && (
        <div
          className="cell-delete"
          onClick={() => {
            Swal.fire({
              title: "Tem certeza que deseja excluir essa variação?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Excluir",
              cancelButtonText: "Cancelar",
            }).then((result) => {
              if (result.isConfirmed) {
                onDeleteVariacao(index);
              }
            });
          }}
        >
          <span></span>
          <span></span>
        </div>
      )}
    </div>
  );
});

interface GradeVariacoesProps {
  variacoes: VariacaoV2[];
  listasDePreco: any[];
  temEstoque: boolean;
  permitir_edicao: boolean;
  tabelasBloqueadas: boolean;
  onUpdateVariacao: (index: number, field: string, value: any) => void;
  onDeleteVariacao: (index: number) => void;
  onRestaurar: () => void;
  onNovaListaDePreco: () => void;
  onReplicar: (field: string, value: any, precoIndex?: number) => void;
}

export const GradeVariacoes: React.FC<GradeVariacoesProps> = ({
  variacoes,
  listasDePreco,
  temEstoque,
  permitir_edicao,
  tabelasBloqueadas,
  onUpdateVariacao,
  onDeleteVariacao,
  onRestaurar,
  onNovaListaDePreco,
  onReplicar,
}) => {

  const { theme } = useTheme();
  const [spinning, setSpinning] = useState(false);
  const [replicado, setReplicado] = useState<string | null>(null);
  const activeVariacoes = variacoes.filter((v) => Number(v.ativo) === 1);

  const truncateText = (text: string) =>
    text.length > 12 ? text.substring(0, 12) + "..." : text;

  const handleReplicarClick = useCallback((key: string, callback: () => void) => {
    callback();
    setReplicado(key);
    setTimeout(() => setReplicado(null), 1500);
  }, []);

  if (activeVariacoes.length === 0) return null;

  return (
    <GradeContainer theme={theme}>
      <div className="grade-tab-titulo">
        <span className="tab-texto fonteSubtitulo">Grade de variações</span>
      </div>
      <div className="grade-subtitulo fonteTexto">
        Atribua as informações específicas para cada uma das variações.
      </div>

      <div className="grade-scroll scrollBonitoBottom">
        {/* Header */}
        <div className="grade-header">
          <div className="header-cell header-cell-lg fonteTexto">SKU | variação</div>
          <div className="header-cell header-cell-lg fonteTexto">Variação</div>
          {temEstoque && <div className="header-cell header-cell-sm fonteTexto">Estoque</div>}
          <div className="header-cell header-cell-sm fonteTexto" title="Informe o peso em gramas. Ex: 1kg = 1000g">Peso (g)</div>
          {listasDePreco.map((lista, idx) => (
            <div className="header-cell fonteTexto" key={idx} title={lista.descricao}>
              Preço {truncateText(lista.descricao)}
            </div>
          ))}
          <div className="nova-lista">
            <div
              className="mais fonteTexto"
              style={!permitir_edicao ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={() => { if (!permitir_edicao) return; onNovaListaDePreco(); }}
            >
              {tabelasBloqueadas ? (
                <LockedIcon
                  mensagem="Recurso disponível a partir do plano Essencial."
                  width="20px"
                  className="blocked"
                />
              ) : (
                <>
                  <Plus className="plus" />
                  Criar e editar
                </>
              )}
            </div>
          </div>
        </div>

        {/* Rows */}
        {variacoes.map((variacao, index) => {
          if (Number(variacao.ativo) !== 1) return null;

          return (
            <GradeRow
              key={`row-${variacao.id}-${index}`}
              variacao={variacao}
              index={index}
              temEstoque={temEstoque}
              permitir_edicao={permitir_edicao}
              listasDePreco={listasDePreco}
              onUpdateVariacao={onUpdateVariacao}
              onDeleteVariacao={onDeleteVariacao}
              onReplicar={onReplicar}
              replicado={replicado}
              onReplicarClick={handleReplicarClick}
              primaryColor={theme.colors.primary}
            />
          );
        })}
      </div>

      {/* Footer */}
      {permitir_edicao && (
        <div className="grade-footer">
          <span
            className={`restaurar fonteTexto ${spinning ? "spinning" : ""}`}
            onClick={() => {
              setSpinning(true);
              onRestaurar();
              setTimeout(() => setSpinning(false), 600);
            }}
          >
            <RestaurarIcon /> Restaurar variações
          </span>
        </div>
      )}
    </GradeContainer>
  );
};
